// Governance, native to Hub (ADR 0028; docs/architecture/BRE-Governance-in-Hub-Plan.md).
//
// The bylaws enacted November 24, 2025 are the rules engine. Every rule Hub
// enforces is a value in GovernanceSettings, with the article and section it
// comes from, and the settings are versioned with an effective date: a
// bylaws amendment is a new settings version, not a code change. §11 of the
// plan records the owner's readings where the bylaws are unclear
// (2026-10-05); those values cite "Owner reading" as well.
//
// The pure rules below (quorum, term limits, officer terms, notice) are
// used by Platform Core to decide and by the staff UI to explain. Core is
// the authority; the UI never decides anything with them.

import { z } from 'zod'
import { addDays, chicagoDate, daysBetween } from './finance-hub'
import type { AgendaItemType, AutoSource, ConsentRequestView } from './governance-agenda'
import type { CarryKind } from './governance-unfinished'
import { GOVERNANCE_FISCAL_CALENDAR, MONTH_NAMES } from './fiscal-calendar'

// --------------------------------------------------------------- vocabulary

export const GOVERNANCE_OFFICES = ['president', 'vice_president', 'past_president', 'secretary', 'treasurer'] as const
export type GovernanceOffice = (typeof GOVERNANCE_OFFICES)[number]
export const OFFICE_LABELS: Record<GovernanceOffice, string> = {
  president: 'President',
  vice_president: 'Vice President',
  past_president: 'Past President',
  secretary: 'Secretary',
  treasurer: 'Treasurer',
}

export const SEAT_KINDS = ['elected', 'appointed'] as const
export type SeatKind = (typeof SEAT_KINDS)[number]
export const SEAT_CLASSES = ['A', 'B', 'C'] as const
export type SeatClass = (typeof SEAT_CLASSES)[number]

export const TERM_BEGAN_BY = ['elected', 'appointed', 'vacancy_appointment', 'override'] as const
export type TermBeganBy = (typeof TERM_BEGAN_BY)[number]
export const TERM_BEGAN_BY_LABELS: Record<TermBeganBy, string> = {
  elected: 'Elected',
  appointed: 'Appointed (City Council)',
  vacancy_appointment: 'Vacancy appointment (partial term)',
  override: 'Elected under a term-limit override',
}
export const TERM_STATUSES = ['active', 'completed', 'resigned', 'removed', 'terminated', 'voided'] as const
export type TermStatus = (typeof TERM_STATUSES)[number]
export const TERM_END_STATUSES = ['resigned', 'removed', 'terminated'] as const
// How past service entered from records ended (ADR 0049).
export const HISTORICAL_TERM_STATUSES = ['completed', 'resigned', 'removed', 'terminated'] as const
export type HistoricalTermStatus = (typeof HISTORICAL_TERM_STATUSES)[number]

export const OFFICER_BEGAN_BY = ['election', 'succession', 'vacancy_election', 'transition'] as const
export type OfficerBeganBy = (typeof OFFICER_BEGAN_BY)[number]
export const OFFICER_STATUSES = ['active', 'completed', 'resigned', 'removed', 'voided'] as const
export type OfficerStatus = (typeof OFFICER_STATUSES)[number]
export const HISTORICAL_OFFICER_STATUSES = ['completed', 'resigned', 'removed'] as const
export type HistoricalOfficerStatus = (typeof HISTORICAL_OFFICER_STATUSES)[number]

export const COMMITTEE_KINDS = ['standing', 'task_force', 'executive', 'nominating'] as const
export type CommitteeKind = (typeof COMMITTEE_KINDS)[number]
export const COMMITTEE_KIND_LABELS: Record<CommitteeKind, string> = { standing: 'Standing committee', task_force: 'Task force', executive: 'Executive Committee', nominating: 'Nominating Committee' }
export const COMMITTEE_ROLES = ['chair', 'member', 'ex_officio_non_voting'] as const
export type CommitteeRole = (typeof COMMITTEE_ROLES)[number]
export const COMMITTEE_ROLE_LABELS: Record<CommitteeRole, string> = { chair: 'Chair', member: 'Member', ex_officio_non_voting: 'Ex officio, non-voting' }

export const MEETING_BODIES = ['board', 'executive', 'committee'] as const
export type MeetingBody = (typeof MEETING_BODIES)[number]
export const MEETING_KINDS = ['regular', 'special', 'annual'] as const
export type MeetingKind = (typeof MEETING_KINDS)[number]
export const MEETING_KIND_LABELS: Record<MeetingKind, string> = { regular: 'Regular', special: 'Special', annual: 'Regular, with the annual meeting' }
export const MEETING_STATUSES = ['scheduled', 'in_session', 'adjourned', 'canceled'] as const
export type MeetingStatus = (typeof MEETING_STATUSES)[number]
// Who called a meeting. A regular (or annual) board meeting is on the
// schedule; a special one names its caller (Article III, Section 6).
export const MEETING_CALLERS = ['schedule', 'president', 'secretary', 'treasurer', 'three_directors', 'committee_chair'] as const
export type MeetingCaller = (typeof MEETING_CALLERS)[number]
export const MEETING_CALLER_LABELS: Record<MeetingCaller, string> = {
  schedule: 'On the schedule',
  president: 'The President',
  secretary: 'The Secretary',
  treasurer: 'The Treasurer',
  three_directors: 'Three directors',
  committee_chair: 'The committee chair',
}
export const NOTICE_METHODS = ['email', 'mail', 'in_person', 'electronic', 'oral'] as const
export type NoticeMethod = (typeof NOTICE_METHODS)[number]
export const NOTICE_METHOD_LABELS: Record<NoticeMethod, string> = { email: 'Email (sent from Hub)', mail: 'Mail', in_person: 'In person, written', electronic: 'Electronic, other', oral: 'Oral' }
export const RSVP_RESPONSES = ['attending_in_person', 'attending_electronically', 'not_attending', 'undecided'] as const
export type RsvpResponse = (typeof RSVP_RESPONSES)[number]
export const ATTENDANCE_STATUSES = ['present_in_person', 'present_electronically', 'absent_excused', 'absent_unexcused'] as const
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number]
export const GOVERNANCE_ATTENDANCE_LABELS: Record<AttendanceStatus, string> = {
  present_in_person: 'Present in person',
  present_electronically: 'Present electronically',
  absent_excused: 'Absent, excused',
  absent_unexcused: 'Absent, unexcused',
}
export const isPresent = (status: AttendanceStatus | null | undefined) => status === 'present_in_person' || status === 'present_electronically'

export const POLICY_CATEGORIES = ['operations', 'governance', 'other'] as const
export type PolicyCategory = (typeof POLICY_CATEGORIES)[number]

// ----------------------------------------------------------------- settings

const source = z.string().min(1).max(200)
const int = (min: number, max: number) => z.number().int().min(min).max(max)

export const VOTE_BASES = ['present_and_voting', 'present', 'board', 'remaining_directors', 'responders'] as const
export const VoteThresholdSchema = z.object({
  motionType: z.string().regex(/^[a-z_]{2,60}$/),
  label: z.string().min(1).max(120),
  numerator: int(1, 100),
  denominator: int(1, 100),
  // "majority" is more than half; a fraction (2/3, 3/4) is at least that share.
  rule: z.enum(['majority', 'at_least']),
  base: z.enum(VOTE_BASES),
  noticeDays: int(0, 60).nullable(),
  source,
})

const EMAIL_AMENDMENT_SOURCE = 'Owner reading, 2026-10-24: an email meeting is a meeting (ADR 0050)'
const CITY_TERM_YEAR_SOURCE = 'Owner reading, 2026-10-25: the City of Burlington appoints for the calendar year (ADR 0056)'
const ED_EMAIL_SEND_SOURCE = 'Owner decision, 2026-10-28: the Executive Director sends on the Secretary’s authorization (ADR 0070)'
// When the City's appointed director's 1-year term runs: the calendar year
// (January 1 to December 31, as the City appoints) or any 1-year span.
export const APPOINTED_TERM_YEARS = ['calendar', 'any'] as const

export const GovernanceSettingsSchema = z.object({
  bylawsEnacted: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  board: z.object({ minDirectors: int(1, 100), maxDirectors: int(1, 100), source }),
  electedTerms: z.object({ years: int(1, 10), maxConsecutive: int(1, 20), classes: z.array(z.enum(SEAT_CLASSES)).min(1), source }),
  appointedSeat: z.object({ appointingBody: z.string().min(1).max(120), years: int(1, 10), maxConsecutive: int(1, 20), source }),
  termLimits: z.object({ partialTermsCount: z.boolean(), absenceResetYears: int(1, 10), overrideThreshold: z.string().min(1), source }),
  fiscalYear: z.object({ startMonth: int(1, 12), termsStartMonthDay: z.string().regex(/^\d{2}-\d{2}$/), source }),
  offices: z.object({
    termYears: int(1, 10),
    evenYearStart: z.array(z.enum(GOVERNANCE_OFFICES)),
    oddYearStart: z.array(z.enum(GOVERNANCE_OFFICES)),
    appointedSeatMayHoldOffice: z.boolean(),
    onePerPerson: z.boolean(),
    source,
  }),
  meetings: z.object({
    minRegularPerFiscalYear: int(0, 52),
    regularNoticeDays: int(0, 60),
    regularNoticeMustBeWritten: z.boolean(),
    specialNoticeHours: int(0, 720),
    specialCallerOffices: z.array(z.enum(GOVERNANCE_OFFICES)),
    specialCallerDirectors: int(1, 20),
    electronicCountsAsPresent: z.boolean(),
    annualAtFinalRegular: z.boolean(),
    source,
  }),
  quorum: z.object({ boardPercent: int(1, 100), committeeRule: z.literal('majority_of_voting_members'), source }),
  absences: z.object({ year: z.literal('fiscal'), countedKinds: z.array(z.enum(MEETING_KINDS)), flagAtUnexcused: int(1, 20), source }),
  committees: z.object({
    presidentExOfficioNonVoting: z.boolean(),
    executiveDirectorExOfficioOn: z.array(z.enum(['executive', 'nominating'])),
    nominatingMinMembers: int(1, 20),
    nominatingChair: z.enum(GOVERNANCE_OFFICES),
    nominatingRequiredOffices: z.array(z.enum(GOVERNANCE_OFFICES)),
    nominatingMembersIneligibleForOffice: z.boolean(),
    executiveMayNotAmendBylaws: z.boolean(),
    source,
  }),
  voteThresholds: z.array(VoteThresholdSchema).min(1),
  emailMeetings: z.object({ senders: z.array(z.enum(GOVERNANCE_OFFICES)), majorityOf: z.literal('responders'), respondersMustBeQuorum: z.boolean(), recordInNextRegularMinutes: z.boolean(), source }),
  conflictOfInterest: z.object({ annualDisclosure: z.boolean(), readers: z.array(z.enum(['executive_director', 'executive_committee'])), source }),
  policies: z.object({ operationsReviewEveryMonths: int(1, 120), source }),
  compliance: z.object({ termEndWarningDays: int(1, 365), source }),
  unanimousWrittenConsent: z.object({ enabled: z.boolean(), source }),
  // Whether a bylaws amendment may go to an email meeting. Added
  // 2026-10-24 (ADR 0050); a version saved before then reads as allowed,
  // the owner's reading.
  bylawsAmendmentByEmail: z.object({ allowed: z.boolean(), source }).default({ allowed: true, source: EMAIL_AMENDMENT_SOURCE }),
  // Added 2026-10-25 (ADR 0056); a version saved before then reads as the
  // calendar year, the owner's reading.
  appointedSeatTermYear: z.object({ year: z.enum(APPOINTED_TERM_YEARS), source }).default({ year: 'calendar', source: CITY_TERM_YEAR_SOURCE }),
  // The board email meetings the Executive Director may send from Hub: on
  // the authorization of an officer holding one of these offices (ADR
  // 0070). Added 2026-10-28; a version saved before then reads as the
  // Secretary, the owner's decision. Empty: the ED never sends one.
  edEmailMeetingSend: z.object({ onAuthorizationOf: z.array(z.enum(GOVERNANCE_OFFICES)), source }).default({ onAuthorizationOf: ['secretary'], source: ED_EMAIL_SEND_SOURCE }),
})
export type GovernanceSettings = z.infer<typeof GovernanceSettingsSchema>
export type VoteThreshold = z.infer<typeof VoteThresholdSchema>

const OWNER = 'Owner reading, 2026-10-05 (plan §11)'

// Version 1, seeded by migration 0045 from exactly this object (a test
// holds the two equal). Every value traces to the bylaws enacted
// November 24, 2025, or to the owner's reading where they are unclear.
export const DEFAULT_GOVERNANCE_SETTINGS: GovernanceSettings = {
  bylawsEnacted: '2025-11-24',
  board: { minDirectors: 10, maxDirectors: 16, source: 'Article III, Section 2' },
  electedTerms: { years: 3, maxConsecutive: 2, classes: ['A', 'B', 'C'], source: 'Article III, Section 3' },
  appointedSeat: { appointingBody: 'City of Burlington', years: 1, maxConsecutive: 3, source: 'Article III, Sections 2.1 and 3.1' },
  termLimits: { partialTermsCount: false, absenceResetYears: 1, overrideThreshold: 'majority_of_board', source: 'Article III, Sections 3, 3.1, 4 and 11' },
  fiscalYear: { startMonth: 7, termsStartMonthDay: '07-01', source: 'Article VII, Section 1' },
  offices: {
    termYears: 2,
    evenYearStart: ['president', 'vice_president', 'treasurer'],
    oddYearStart: ['secretary', 'past_president'],
    appointedSeatMayHoldOffice: false,
    onePerPerson: true,
    source: 'Article IV, Sections 1-2',
  },
  meetings: {
    minRegularPerFiscalYear: 6,
    regularNoticeDays: 10,
    regularNoticeMustBeWritten: true,
    specialNoticeHours: 24,
    specialCallerOffices: ['president', 'secretary', 'treasurer'],
    specialCallerDirectors: 3,
    electronicCountsAsPresent: true,
    annualAtFinalRegular: true,
    source: 'Article III, Sections 6-9',
  },
  quorum: { boardPercent: 51, committeeRule: 'majority_of_voting_members', source: `Article III, Section 5; committees: ${OWNER}, item 4` },
  absences: { year: 'fiscal', countedKinds: ['regular', 'special', 'annual'], flagAtUnexcused: 3, source: `Article III, Section 10; year and meetings counted: ${OWNER}, item 1` },
  committees: {
    presidentExOfficioNonVoting: true,
    executiveDirectorExOfficioOn: ['executive', 'nominating'],
    nominatingMinMembers: 4,
    nominatingChair: 'vice_president',
    nominatingRequiredOffices: ['vice_president', 'past_president'],
    nominatingMembersIneligibleForOffice: true,
    executiveMayNotAmendBylaws: true,
    source: 'Article IV, Section 5a; Article V, Sections 1-3',
  },
  voteThresholds: [
    { motionType: 'ordinary', label: 'Ordinary business', numerator: 1, denominator: 2, rule: 'majority', base: 'present_and_voting', noticeDays: null, source: "Robert's Rules (Article VII, Section 2)" },
    { motionType: 'elect', label: 'Elect directors and officers', numerator: 1, denominator: 2, rule: 'majority', base: 'present', noticeDays: null, source: 'Article III, Section 4' },
    { motionType: 'term_limit_override', label: 'Override a term limit', numerator: 1, denominator: 2, rule: 'majority', base: 'board', noticeDays: null, source: 'Article III, Sections 3 and 3.1' },
    { motionType: 'fill_director_vacancy', label: "Fill a director vacancy (the President's appointment)", numerator: 2, denominator: 3, rule: 'at_least', base: 'board', noticeDays: null, source: 'Article III, Section 11' },
    { motionType: 'remove', label: 'Remove a director or an officer', numerator: 3, denominator: 4, rule: 'at_least', base: 'remaining_directors', noticeDays: null, source: `Article III, Section 10; also governs Article IV, Section 3: ${OWNER}, item 3` },
    { motionType: 'terminate_absences', label: 'Terminate for excess absences', numerator: 1, denominator: 2, rule: 'majority', base: 'present_and_voting', noticeDays: null, source: 'Article III, Section 10' },
    { motionType: 'create_standing_committee', label: 'Create a standing committee', numerator: 1, denominator: 2, rule: 'majority', base: 'board', noticeDays: null, source: 'Article V, Section 1' },
    { motionType: 'amend_bylaws', label: 'Amend the bylaws', numerator: 2, denominator: 3, rule: 'at_least', base: 'present', noticeDays: 10, source: 'Article X' },
    { motionType: 'past_president_at_large', label: 'Fill the Past President seat as a Director at Large', numerator: 1, denominator: 2, rule: 'majority', base: 'board', noticeDays: null, source: 'Article IV, Section 5c' },
  ],
  emailMeetings: { senders: ['secretary', 'president'], majorityOf: 'responders', respondersMustBeQuorum: true, recordInNextRegularMinutes: true, source: `Article III, Section 9; majority: ${OWNER}, item 2` },
  conflictOfInterest: { annualDisclosure: true, readers: ['executive_director', 'executive_committee'], source: `Not in the bylaws (IRS Form 990 best practice): ${OWNER}, item 6; readers: owner decision 2026-10-05` },
  policies: { operationsReviewEveryMonths: 12, source: 'Article VI, Section 1' },
  compliance: { termEndWarningDays: 90, source: 'Plan §6.10' },
  unanimousWrittenConsent: { enabled: false, source: `${OWNER}, item 5` },
  bylawsAmendmentByEmail: { allowed: true, source: EMAIL_AMENDMENT_SOURCE },
  appointedSeatTermYear: { year: 'calendar', source: CITY_TERM_YEAR_SOURCE },
  edEmailMeetingSend: { onAuthorizationOf: ['secretary'], source: ED_EMAIL_SEND_SOURCE },
}

// ------------------------------------------------------------ date helpers

// BRE's fiscal calendar by date lives in ./fiscal-calendar (ADR 0050), shared
// by governance, grants and every other year-based report. Re-exported here so
// existing imports from './governance' keep working.
export * from './fiscal-calendar'

// A settings version must agree with the calendar from the day it takes
// effect: the start month and the day terms begin are the current
// segment's (July 1).
export function fiscalCalendarProblems(settings: Pick<GovernanceSettings, 'fiscalYear'>): string[] {
  const current = GOVERNANCE_FISCAL_CALENDAR[GOVERNANCE_FISCAL_CALENDAR.length - 1]!
  const problems: string[] = []
  const mm = String(current.startMonth).padStart(2, '0')
  if (settings.fiscalYear.startMonth !== current.startMonth)
    problems.push(`The fiscal year starts in ${MONTH_NAMES[current.startMonth - 1]}. Changing it needs a dated change to Hub's fiscal calendar, not only a new settings version`)
  if (settings.fiscalYear.termsStartMonthDay !== `${mm}-01`) problems.push(`Terms begin on the first day of the fiscal year (${MONTH_NAMES[current.startMonth - 1]} 1)`)
  return problems
}

// --------------------------------------------------------- bylaws versions

// Every set of bylaws BRE (and AMF before it) has had, by the day it took
// effect (ADR 0050, from the corporate documents review of 2026-10-24).
// Only the last is loaded into the rules engine (settings version 1); the
// earlier ones are reference records: which rules a past meeting ran under,
// for labels and the historical import. Migration 0065 seeds the same rows
// into `bylaws_version` (a test holds the two equal), where each can be
// linked to its document later.
export type BylawsVersionRef = { id: string; effectiveOn: string; label: string; rulesInHub: boolean; note: string }
export const BYLAWS_VERSIONS: readonly BylawsVersionRef[] = [
  { id: 'byv_1995_amf_articles', effectiveOn: '1995-03-06', label: 'AMF articles only', rulesInHub: false, note: 'Articles of incorporation filed March 6, 1995; no bylaws before January 18, 2018' },
  { id: 'byv_2018_01_18', effectiveOn: '2018-01-18', label: 'AMF bylaws of January 2018', rulesInHub: false, note: 'Original bylaws dated January 18, 2018' },
  { id: 'byv_2020_07_23', effectiveOn: '2020-07-23', label: 'Bylaws with the revisions of July 2020', rulesInHub: false, note: 'Name change and revisions adopted July 23, 2020; text not on file' },
  { id: 'byv_2021_02_25', effectiveOn: '2021-02-25', label: 'Bylaws with the revisions of February 2021', rulesInHub: false, note: 'Revisions adopted February 25, 2021' },
  { id: 'byv_2023_04_03', effectiveOn: '2023-04-03', label: 'Bylaws with the 2023 amendments', rulesInHub: false, note: 'Seven amendments adopted by email vote closing April 3, 2023 (the document header says March 20, 2023)' },
  { id: 'byv_2024_02_22', effectiveOn: '2024-02-22', label: 'Bylaws of February 22, 2024', rulesInHub: false, note: 'Repealed and replaced the earlier bylaws; signed' },
  { id: 'byv_2025_11_24', effectiveOn: '2025-11-24', label: 'Bylaws enacted November 24, 2025', rulesInHub: true, note: 'In force; the rules in governance settings version 1. Signed copies on file (owner, 2026-10-24)' },
]
export const CURRENT_BYLAWS = BYLAWS_VERSIONS[BYLAWS_VERSIONS.length - 1]!

// The bylaws in force on a day (the AMF articles for anything earlier than
// the first record).
export function bylawsVersionOn(day: string): BylawsVersionRef {
  let found = BYLAWS_VERSIONS[0]!
  for (const v of BYLAWS_VERSIONS) if (v.effectiveOn <= day) found = v
  return found
}
// The era label the historical import stores on a meeting from records.
export const bylawsEraOn = (day: string) => bylawsVersionOn(day).label

// The rules for a day before the 2025 bylaws: Hub has only the 2025 rules,
// so it uses them as a stand-in and says so in every source shown, instead
// of citing the 2025 bylaws as if they had been in force then.
export function standInSettings(settings: GovernanceSettings, day: string): GovernanceSettings {
  const then = bylawsVersionOn(day)
  if (then.rulesInHub) return settings
  const note = (s: string) => `Stand-in: the 2025 rules (${s}); in force then: ${then.label}`.slice(0, 200)
  const out = structuredClone(settings) as unknown as Record<string, unknown>
  for (const v of Object.values(out)) if (v && typeof v === 'object' && !Array.isArray(v) && typeof (v as { source?: unknown }).source === 'string') (v as { source: string }).source = note((v as { source: string }).source)
  return { ...(out as unknown as GovernanceSettings), voteThresholds: settings.voteThresholds.map((t) => ({ ...t, source: note(t.source) })) }
}

// The same calendar day n years later (Feb 29 becomes Feb 28).
export function addYears(day: string, years: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  const last = new Date(Date.UTC(y + years, m, 0)).getUTCDate()
  return `${y + years}-${String(m).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`
}

// A venue-local YYYY-MM-DDTHH:MM as an instant (ms since the epoch),
// honoring daylight saving time in America/Chicago.
export function chicagoLocalToUtcMs(local: string): number {
  const asUtc = Date.parse(`${local}:00Z`)
  const offsetAt = (ms: number) => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
        .formatToParts(new Date(ms))
        .map((p) => [p.type, p.value]),
    )
    return Date.parse(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:00Z`) - ms
  }
  const first = asUtc - offsetAt(asUtc)
  return asUtc - offsetAt(first)
}

// ------------------------------------------------------------------- quorum

// Article III, Section 5: "a simple majority (51%)" of the Board of
// Directors, counted against the directors seated on the meeting date.
// 51% rounded up is never less than a strict majority, and for 10 to 16
// seated directors the two agree: 6, 6, 7, 7, 8, 8, 9.
export function boardQuorum(seated: number, percent = DEFAULT_GOVERNANCE_SETTINGS.quorum.boardPercent): number {
  if (seated <= 0) return 0
  return Math.max(Math.floor(seated / 2) + 1, Math.floor((seated * percent + 99) / 100))
}

// A committee, the Executive Committee included: a majority of its voting
// members (owner reading, plan §11 item 4). Non-voting members never count.
export function committeeQuorum(votingMembers: number): number {
  return votingMembers <= 0 ? 0 : Math.floor(votingMembers / 2) + 1
}

// -------------------------------------------------------------- term limits

export type TermForLimit = { seatKind: SeatKind; startsOn: string; lastDay: string; partial: boolean }
export type TermLimitResult = {
  eligible: boolean
  // Full terms of this kind served back to back up to the proposed start,
  // with no break of a year or more off the board.
  consecutiveFullTerms: number
  maxConsecutive: number
  // Where the run being counted began, and why counting stopped.
  runStartsOn: string | null
  resetBy: 'absence' | 'none'
  reason: string
}

// Article III, Sections 3 and 3.1: elected directors serve at most two
// consecutive three-year terms; the City's appointed director at most
// three consecutive one-year terms. Partial terms filling a vacancy don't
// count (Sections 4 and 11). After a year off the board a director is
// eligible again. Board service of either kind keeps a run going; only
// full terms of the same kind are counted.
export function termLimitStatus(terms: TermForLimit[], kind: SeatKind, proposedStart: string, settings: GovernanceSettings = DEFAULT_GOVERNANCE_SETTINGS): TermLimitResult {
  const maxConsecutive = kind === 'elected' ? settings.electedTerms.maxConsecutive : settings.appointedSeat.maxConsecutive
  const earlier = terms.filter((t) => t.lastDay < proposedStart).sort((a, b) => (a.startsOn < b.startsOn ? 1 : -1))
  let cursor = proposedStart
  let count = 0
  let runStartsOn: string | null = null
  let resetBy: TermLimitResult['resetBy'] = 'none'
  for (const term of earlier) {
    // Back on the board on `cursor`; off it from the day after lastDay.
    const offFrom = addDays(term.lastDay, 1)
    if (cursor >= addYears(offFrom, settings.termLimits.absenceResetYears)) {
      resetBy = 'absence'
      break
    }
    if (term.seatKind === kind && (settings.termLimits.partialTermsCount || !term.partial)) count += 1
    runStartsOn = term.startsOn
    cursor = term.startsOn
  }
  const eligible = count < maxConsecutive
  const what = kind === 'elected' ? 'elected' : 'appointed'
  const reason = eligible
    ? `${count} consecutive full ${what} term${count === 1 ? '' : 's'} before ${proposedStart}; the limit is ${maxConsecutive}`
    : `Already served ${count} consecutive full ${what} terms (the limit is ${maxConsecutive}); eligible again after a year off the board, or by a majority-vote override`
  return { eligible, consecutiveFullTerms: count, maxConsecutive, runStartsOn, resetBy, reason }
}

// The shape of a director term (problems only; empty means it fits).
// The default last day of a term starting then on a seat of this kind.
export function defaultTermEnd(seatKind: SeatKind, startsOn: string, settings: GovernanceSettings = DEFAULT_GOVERNANCE_SETTINGS): string {
  if (seatKind === 'appointed' && (settings.appointedSeatTermYear?.year ?? 'calendar') === 'calendar') return `${String(Number(startsOn.slice(0, 4)) + settings.appointedSeat.years - 1).padStart(4, '0')}-12-31`
  return addDays(addYears(startsOn, seatKind === 'elected' ? settings.electedTerms.years : settings.appointedSeat.years), -1)
}

export function directorTermProblems(
  term: { seatKind: SeatKind; startsOn: string; endsOn: string; beganBy: TermBeganBy; partial: boolean; transitionExtended: boolean },
  settings: GovernanceSettings = DEFAULT_GOVERNANCE_SETTINGS,
): string[] {
  const problems: string[] = []
  if (term.endsOn < term.startsOn) problems.push('A term must end on or after its first day')
  if (term.beganBy === 'vacancy_appointment' && !term.partial) problems.push('A vacancy appointment is a partial term')
  if (term.seatKind === 'appointed') {
    if (term.beganBy !== 'appointed' && term.beganBy !== 'override' && term.beganBy !== 'vacancy_appointment') problems.push("The City's seat is filled by appointment")
    if (term.endsOn > addDays(addYears(term.startsOn, settings.appointedSeat.years), -1) && !term.transitionExtended) problems.push(`The appointed director's term is ${settings.appointedSeat.years} year`)
    // The City appoints for the calendar year: a full term is January 1 to
    // December 31; a partial one ends December 31 at the latest.
    if ((settings.appointedSeatTermYear?.year ?? 'calendar') === 'calendar' && !term.transitionExtended) {
      const yearEnd = `${term.startsOn.slice(0, 4)}-12-31`
      if (term.beganBy === 'vacancy_appointment' || term.partial) {
        if (term.endsOn > yearEnd) problems.push("A partial term on the City's seat ends December 31 at the latest (the City appoints for the calendar year)")
      } else if (term.startsOn.slice(5) !== '01-01' || term.endsOn !== yearEnd) problems.push("The City's seat runs on the calendar year: a term is January 1 to December 31")
    }
    return problems
  }
  if (term.beganBy === 'appointed') problems.push('An elected seat is filled by election or a vacancy appointment')
  const full = !term.partial && !term.transitionExtended && (term.beganBy === 'elected' || term.beganBy === 'override')
  if (full) {
    if (term.startsOn.slice(5) !== settings.fiscalYear.termsStartMonthDay) problems.push(`Elected terms begin ${settings.fiscalYear.termsStartMonthDay === '07-01' ? 'July 1' : settings.fiscalYear.termsStartMonthDay}, the first day of the fiscal year`)
    if (term.endsOn !== addDays(addYears(term.startsOn, settings.electedTerms.years), -1)) problems.push(`An elected term is ${settings.electedTerms.years} years`)
  }
  if (term.partial && term.endsOn > addDays(addYears(term.startsOn, settings.electedTerms.years), -1)) problems.push('A partial term ends when the vacated term would have')
  return problems
}

// ---------------------------------------------------------------- officers

export function officerTermProblems(
  term: { office: GovernanceOffice; startsOn: string; endsOn: string; beganBy: OfficerBeganBy },
  settings: GovernanceSettings = DEFAULT_GOVERNANCE_SETTINGS,
): string[] {
  const problems: string[] = []
  if (term.endsOn < term.startsOn) problems.push('A term must end on or after its first day')
  const maxEnd = addDays(addYears(term.startsOn, settings.offices.termYears), -1)
  if (term.endsOn > maxEnd && term.beganBy !== 'transition') problems.push(`An officer's term is ${settings.offices.termYears} years`)
  if (term.beganBy === 'election') {
    if (term.startsOn.slice(5) !== settings.fiscalYear.termsStartMonthDay) problems.push('Officers elected at the annual meeting begin July 1')
    if (term.endsOn !== maxEnd) problems.push(`An elected officer serves ${settings.offices.termYears} years`)
    const year = Number(term.startsOn.slice(0, 4))
    const even = settings.offices.evenYearStart.includes(term.office)
    const odd = settings.offices.oddYearStart.includes(term.office)
    if (even && year % 2 !== 0) problems.push(`The ${OFFICE_LABELS[term.office]}'s term begins in an even year`)
    if (odd && year % 2 !== 1) problems.push(`The ${OFFICE_LABELS[term.office]}'s term begins in an odd year`)
  }
  return problems
}

// ------------------------------------------------------------------- notice

export type NoticeCheck = { required: boolean; ok: boolean; reason: string }

// Article III, Sections 6-8. A regular meeting (the annual meeting is the
// final regular meeting of the year) needs written notice at least 10 days
// before, counted in Burlington calendar days, of the time it is held at. A
// special meeting needs notice, oral or written, at least 24 hours before.
// Committee and Executive Committee meetings have no notice rule in the
// bylaws, so Hub records notice for them but doesn't require it.
export function noticeQualifies(
  meeting: { body: MeetingBody; kind: MeetingKind; startsLocal: string },
  notice: { method: NoticeMethod; sentAt: string; announcedStartsLocal: string } | null,
  settings: GovernanceSettings = DEFAULT_GOVERNANCE_SETTINGS,
): NoticeCheck {
  if (meeting.body !== 'board') return { required: false, ok: true, reason: 'No notice period for committee meetings' }
  if (!notice) return { required: true, ok: false, reason: meeting.kind === 'special' ? 'No notice recorded' : 'No written notice recorded' }
  if (notice.announcedStartsLocal !== meeting.startsLocal) return { required: true, ok: false, reason: 'The notice announced a different time' }
  if (meeting.kind === 'special') {
    const hours = (chicagoLocalToUtcMs(meeting.startsLocal) - Date.parse(notice.sentAt)) / 3_600_000
    return hours >= settings.meetings.specialNoticeHours
      ? { required: true, ok: true, reason: `Notice ${Math.floor(hours)} hours before (at least ${settings.meetings.specialNoticeHours})` }
      : { required: true, ok: false, reason: `Notice only ${Math.max(0, Math.floor(hours))} hours before; a special meeting needs ${settings.meetings.specialNoticeHours}` }
  }
  if (settings.meetings.regularNoticeMustBeWritten && notice.method === 'oral') return { required: true, ok: false, reason: 'A regular meeting needs written notice' }
  const days = daysBetween(chicagoDate(new Date(notice.sentAt)), meeting.startsLocal.slice(0, 10))
  return days >= settings.meetings.regularNoticeDays
    ? { required: true, ok: true, reason: `Written notice ${days} days before (at least ${settings.meetings.regularNoticeDays})` }
    : { required: true, ok: false, reason: `Written notice only ${Math.max(0, days)} days before; a regular meeting needs ${settings.meetings.regularNoticeDays}` }
}

// ------------------------------------------------------------------ shapes

export type GovernanceSettingsVersion = {
  id: string
  version: number
  effectiveOn: string
  settings: GovernanceSettings
  source: string
  note: string | null
  createdByName: string | null
  createdAt: string
  withdrawnAt: string | null
  state: 'current' | 'scheduled' | 'superseded' | 'withdrawn'
}

export type PersonRef = { id: string; name: string }

export type BoardTermView = {
  id: string
  person: PersonRef
  seatId: string
  seatLabel: string
  seatKind: SeatKind
  seatClass: SeatClass | null
  startsOn: string
  endsOn: string
  lastDay: string
  beganBy: TermBeganBy
  partial: boolean
  transitionExtended: boolean
  status: TermStatus
  endedOn: string | null
  endReason: string | null
  appointmentReference: string | null
  overrideId: string | null
  note: string | null
  onboardingStampId: string | null
  version: number
  // Entered from records (ADR 0049): past service, entered already ended.
  fromRecords: boolean
  sourceRef: string | null
}

// Which board entries can be voided, as entered by mistake: past service
// from records (ADR 0049 §3), and a term or office that hasn't started yet
// (owner, 2026-10-26). Anything else that is wrong is ended instead. The
// void routes are the same for both; null when it can't be voided.
export type VoidableBecause = 'from_records' | 'not_started'
export function voidableBecause(entry: { status: string; fromRecords: boolean; startsOn: string }, today: string): VoidableBecause | null {
  if (entry.status === 'voided') return null
  if (entry.fromRecords) return 'from_records'
  if (entry.status === 'active' && entry.startsOn > today) return 'not_started'
  return null
}

export type TermLimitView = TermLimitResult & { nextTermStartsOn: string; override: { id: string; note: string; motionId: string | null } | null }

export type BoardSeatView = {
  id: string
  label: string
  kind: SeatKind
  seatClass: SeatClass | null
  appointingBody: string | null
  retiredAt: string | null
  current: BoardTermView | null
  upcoming: BoardTermView | null
  termLimit: TermLimitView | null
}

export type OfficerTermView = {
  id: string
  person: PersonRef
  office: GovernanceOffice
  startsOn: string
  endsOn: string
  lastDay: string
  beganBy: OfficerBeganBy
  status: OfficerStatus
  endedOn: string | null
  endReason: string | null
  note: string | null
  fromRecords: boolean
  sourceRef: string | null
}

export type BoardView = {
  on: string
  settingsVersion: number
  seated: number
  minDirectors: number
  maxDirectors: number
  quorum: number
  warnings: string[]
  seats: BoardSeatView[]
  terms: BoardTermView[]
  officers: OfficerTermView[]
  executiveDirector: PersonRef | null
}

// One person's board service, past and present (the Person page; ADR
// 0049): director terms, offices and committee seats, each marked when it
// was entered from records, and whether they are the Executive Director
// now (a separate role, often on a separate person record).
export type PersonBoardService = {
  personId: string
  terms: BoardTermView[]
  officers: OfficerTermView[]
  committees: { id: string; committeeId: string; committeeName: string; role: CommitteeRole; startsOn: string; endedOn: string | null; fromRecords: boolean; sourceRef: string | null; voidedAt: string | null }[]
  executiveDirector: boolean
}

export type CommitteeMemberView = {
  id: string | null
  person: PersonRef
  role: CommitteeRole
  voting: boolean
  // Stored members have a row; derived ones (officers on the Executive
  // Committee, the President and the ED ex officio) come from their office.
  derivedFrom: string | null
  startsOn: string | null
  endedOn: string | null
  ineligibleForOffice: boolean
  note: string | null
  // An appointment's planned last day (ADR 0059).
  endsOn?: string | null
}

export type CommitteeView = {
  id: string
  name: string
  kind: CommitteeKind
  description: string | null
  createdByKind: 'bylaws' | 'board_motion' | 'president_appointment'
  authorizedNote: string | null
  executiveApprovalOn: string | null
  status: 'active' | 'dissolved'
  members: CommitteeMemberView[]
  votingCount: number
  quorum: number
  warnings: string[]
  // ADR 0059: decides on its own or recommends to the board (not shown for
  // the Executive Committee, whose authority is the bylaws'), the board
  // resolution that gave it, and the committee register's series.
  authority: 'recommends' | 'decides'
  authorityResolution: { id: string; number: string } | null
  authorityNote: string | null
  registerPrefix: string | null
  registerPrefixDefault: string
  pendingAppointments: number
}

export type MeetingSummary = {
  id: string
  title: string
  body: MeetingBody
  committeeId: string | null
  committeeName: string | null
  kind: MeetingKind
  startsLocal: string
  endsLocal: string | null
  location: string | null
  status: MeetingStatus
  seriesId: string | null
  notice: NoticeCheck
}

export type AgendaItemView = {
  id: string
  position: number
  executive: boolean
  // For a viewer without governance.confidential, an executive item reads
  // "Executive session" and nothing else.
  redacted: boolean
  title: string
  description: string | null
  presenterPersonId: string | null
  presenterName: string | null
  durationMinutes: number | null
  // In the order staff set (ADR 0051); kind, type and size for the
  // meeting screen.
  documents: { linkId: string; documentId: string; title: string; classification: string; currentVersion: number | null; kind: 'file' | 'link'; contentType: string | null; sizeBytes: number | null }[]
  // ADR 0031: what the item is, the group it sits in (one level), whether
  // Hub added it on its own (and from what), the minutes it approves, and
  // a removal from the consent agenda during the meeting.
  itemType: AgendaItemType
  parentId: string | null
  autoSource: AutoSource | null
  autoLabel: string | null
  minutesId: string | null
  consentRemoval: { requestedBy: PersonRef; at: string } | null
  // 2026-10-26: directors' open requests (from myBRE) that a consent item
  // be discussed separately, before the call to order; and why an item may
  // not sit on the consent agenda (a financial report), when it does.
  consentRequests: ConsentRequestView[]
  consentProblem: string | null
  // ADR 0065: an item carried here from an earlier meeting (or referred, or
  // returned), with its label.
  carry?: { id: string; kind: CarryKind; label: string } | null
}

export type NoticeView = {
  id: string
  method: NoticeMethod
  sentAt: string
  sentByName: string | null
  announcedStartsLocal: string
  recipientCount: number
  recipients: PersonRef[]
  subject: string | null
  attestation: string | null
  deliveries: { personId: string; outcome: string; at: string }[]
  check: NoticeCheck
}

export type ExpectedAttendee = {
  person: PersonRef
  voting: boolean
  capacity: string
  rsvp: { response: RsvpResponse; excuseRequestedAt: string | null; onBehalf: boolean } | null
  attendance: { id: string; status: AttendanceStatus; amended: boolean; note: string | null } | null
}

export type QuorumSnapshotView = { id: string; reason: string; takenAt: string; votingSeated: number; presentVoting: number; required: number; hasQuorum: boolean; rule: string; settingsVersion: number }

export type PacketSummary = { id: string; version: number; publishedAt: string; publishedByName: string | null; note: string | null; hash: string; items: number; documents: number; acknowledgments: number }

export type MeetingView = MeetingSummary & {
  virtualLink: string | null
  canSeeVirtualLink: boolean
  calledBy: MeetingCaller | null
  calledByName: string | null
  callConfirmations: { person: PersonRef; confirmedAt: string; onBehalf: boolean; attestation: string | null }[]
  callComplete: boolean
  calledToOrderAt: string | null
  adjournedAt: string | null
  canceledAt: string | null
  cancelReason: string | null
  hasExecutiveSession: boolean
  notices: NoticeView[]
  agenda: AgendaItemView[]
  packets: PacketSummary[]
  expected: ExpectedAttendee[]
  guests: { id: string; name: string; personId: string | null; affiliation: string | null; note: string | null }[]
  quorum: { votingSeated: number; required: number; presentVoting: number; hasQuorum: boolean; rule: string }
  snapshots: QuorumSnapshotView[]
  actionItems: { id: string; taskId: string; title: string; status: string; assigneeName: string | null; assigneePersonId: string | null; dueOn: string | null; agendaItemId: string | null }[]
  version: number
  // ADR 0031: the agenda's planned minutes; whether the agenda or its
  // documents changed since the latest packet version (governance.manage);
  // the conductor's timing; and open action items from earlier meetings.
  totalMinutes: number
  packetChanged: boolean | null
  timing: { currentItemId: string | null; segments: { agendaItemId: string; startedAt: string; endedAt: string | null }[] }
  openActionItems: { id: string; taskId: string; title: string; status: string; assigneeName: string | null; dueOn: string | null; meetingId: string; meetingTitle: string }[]
}

export type AbsenceRow = { person: PersonRef; fiscalYear: number; unexcused: number; excused: number; meetings: number; flagged: boolean }

export type ComplianceCheck = { code: string; title: string; source: string; status: 'ok' | 'due' | 'warning' | 'not_met' | 'not_applicable'; detail: string; requirementId: string | null; requirementStatus: string | null; dueOn: string | null }

export type PolicyView = {
  id: string
  title: string
  category: PolicyCategory
  description: string | null
  reviewEveryMonths: number | null
  status: 'active' | 'retired'
  current: { id: string; version: number; documentId: string | null; documentVersion: number | null; documentTitle: string | null; adoptedOn: string; effectiveOn: string; reviewDueOn: string | null; summary: string | null; adoptionReference: string | null } | null
  versions: { id: string; version: number; adoptedOn: string; effectiveOn: string; reviewDueOn: string | null; acknowledgments: number; requested: number }[]
  reviews: { id: string; reviewedOn: string; outcome: string; note: string | null; nextReviewDueOn: string | null; recordedByName: string | null }[]
  reviewDueOn: string | null
  reviewOverdue: boolean
  // The resolution book (ADR 0048): adopted by, amended by, retired by.
  resolutions: { id: string; number: string; title: string; effect: 'adopted' | 'amended' | 'retired'; adoptedOn: string }[]
}

export type GovernanceOverview = {
  today: string
  fiscalYear: number
  settingsVersion: number
  board: { seated: number; minDirectors: number; maxDirectors: number; quorum: number; cityFilled: boolean; warnings: string[] }
  nextMeeting: MeetingSummary | null
  upcoming: MeetingSummary[]
  regularMeetingsThisYear: number
  termsEnding: { kind: 'director' | 'officer'; person: PersonRef; label: string; lastDay: string }[]
  absenceFlags: AbsenceRow[]
  compliance: { ok: number; attention: number; checks: ComplianceCheck[] }
  policiesDue: { id: string; title: string; reviewDueOn: string }[]
  canManage: boolean
  canConfidential: boolean
  // Phase 3 (ADR 0030).
  resignationsPending: { id: string; person: PersonRef; scope: 'board' | 'office'; office: GovernanceOffice | null; effectiveOn: string; status: 'delivered' | 'received' | 'effective' | 'withdrawn' }[]
  vacanciesOpen: { id: string; kind: 'director' | 'office'; label: string; openedOn: string; termEndsOn: string }[]
  elections: { id: string; title: string; status: string; rehearsal: boolean }[]
}
